import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "layouts/Main";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchProductionForecastGroups } from "store/appSlice";
import { ProductionForecastGroup } from "api/models/ProductionForecast";
import ProductionForecastGroupsTable from "./ProductionForecastGroupsTable";
import { selectProductionForecastGroups } from "store/selectors";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormControl } from "@mui/material";
import { ProtectedPaths } from "routes";

interface Row {
  date: string;
  forecasts: ProductionForecastGroup[];
}

const ProductionForecastNew = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [formattedDates, setFormattedDates] = useState([]);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useAppDispatch();
  const forecastGroups = useAppSelector(selectProductionForecastGroups);

  useEffect(() => {
    const fromDateParam = searchParams.get("from");
    const toDateParam = searchParams.get("to");

    if (fromDateParam) {
      setFromDate(dayjs(fromDateParam));
    } else {
      setFromDate(dayjs().subtract(3, "day"));
    }

    if (toDateParam) {
      setToDate(dayjs(toDateParam));
    } else {
      setToDate(dayjs().add(3, "day"));
    }
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      navigate(
        `/${ProtectedPaths.Production}?from=${dayjs(fromDate).format("YYYY-MM-DD")}&to=${dayjs(toDate).format("YYYY-MM-DD")}`,
        {
          replace: true,
        },
      );
      dispatch(
        fetchProductionForecastGroups(
          dayjs(fromDate).format("YYYY-MM-DD"),
          dayjs(toDate).format("YYYY-MM-DD"),
        ),
      );
    }
  }, [fromDate, toDate, dispatch]);

  useEffect(() => {
    const start = dayjs(fromDate).startOf("day");
    const end = dayjs(toDate).startOf("day");
    const dates = [];

    const totalDays = end.diff(start, "day");

    for (let i = 0; i <= totalDays; i++) {
      dates.push(start.add(i, "day").format("YYYY-MM-DD"));
    }

    setFormattedDates(dates);
  }, [fromDate, toDate]);

  const rows: Row[] = useMemo(() => {
    if (!formattedDates || !forecastGroups) return [];

    const tempRows: Row[] = [];

    formattedDates.forEach((date) => {
      const matchingGroups = forecastGroups.filter(
        (group) => group.forecastDate === date,
      );
      tempRows.push({
        date,
        forecasts: matchingGroups,
      });
    });

    forecastGroups.forEach((group) => {
      if (!tempRows.find((row) => row.date === group.forecastDate)) {
        tempRows.push({
          date: group.forecastDate,
          forecasts: [group],
        });
      }
    });

    return tempRows.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  }, [formattedDates, forecastGroups]);

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  return (
    <MainLayout
      snackbar={{
        open: snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Typography
        variant="h5"
        sx={{ position: "relative", maxWidth: "fit-content", mb: 2 }}
      >
        Production forecasts
      </Typography>
      <Box sx={{ mb: 2 }}>
        <FormControl
          sx={{
            minWidth: 280,
            maxWidth: 280,
            background: "white",
            mr: 1,
          }}
          size="small"
        >
          <DatePicker
            slotProps={{ textField: { size: "small" } }}
            name="fromDate"
            value={fromDate}
            onChange={(value) => {
              setFromDate(dayjs(value));
            }}
            label="From date"
          />
        </FormControl>
        <FormControl
          sx={{
            minWidth: 280,
            maxWidth: 280,
            background: "white",
          }}
          size="small"
        >
          <DatePicker
            slotProps={{ textField: { size: "small" } }}
            name="toDate"
            value={toDate}
            onChange={(value) => {
              setToDate(dayjs(value));
            }}
            label="To date"
          />
        </FormControl>
      </Box>
      <Box sx={{ mt: "1rem" }}>
        {rows &&
          rows.reverse().map((row) => (
            <Accordion
              defaultExpanded={dayjs(row.date).isTomorrow()}
              sx={{ mb: "1rem", boxShadow: 2 }}
              key={"ForecastSampleAccordion-" + row.date}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#eee",
                }}
              >
                <Typography
                  component="h6"
                  variant="h6"
                  fontSize="1.1rem"
                  fontWeight="400"
                >
                  {`${dayjs(row.date).format("DD.MM.YYYY")} - ${dayjs(
                    row.date,
                  ).format("dddd")}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ProductionForecastGroupsTable
                  forecastGroups={row.forecasts}
                  date={row.date}
                  setSnackbar={setSnackbar}
                  dispatch={() =>
                    dispatch(
                      fetchProductionForecastGroups(
                        dayjs(fromDate).format("YYYY-MM-DD"),
                        dayjs(toDate).format("YYYY-MM-DD"),
                      ),
                    )
                  }
                />
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>
    </MainLayout>
  );
};

export default ProductionForecastNew;
